import { FC } from 'react';
import {
  FinancialSecuritiesParams,
  RelationsParams,
} from 'src/apis/contract-management/types';
import { ContractManagementRequest } from 'src/apis/contract-management/use-contract-management';
import { useFinancialSecurities } from 'src/apis/contract-management/use-financial-securities';
import {
  OperatorRequest,
  useOperator,
} from 'src/apis/contract-management/use-operator';
import { OperatorRequest as CaMaOperatorRequest } from 'src/apis/contract-management/use-operator-by-tso';
import { useOperatorRelations } from 'src/apis/contract-management/use-operator-relations';
import { OrganisationAssignmentsRequest } from 'src/apis/monolith/use-organisation-assignments';
import { UserAssignmentRequest } from 'src/apis/monolith/use-user-assignment';
import { TsoDocumentList } from 'src/components/domain-specifics/tso-document-list';
import { ThrowNotFound } from 'src/components/feedback/not-found';
import { Stack } from 'src/components/layout/stack';
import { PageSpinner } from 'src/components/spinner-container';
import { Heading } from 'src/components/text/heading';
import { Successful } from 'src/hooks/use-axios';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { useReferrer } from 'src/hooks/use-referrer2';
import { useTitle } from 'src/hooks/use-title';
import { TsoRequest } from 'src/pages/market-information/players/operators-detail/use-tso';
import { AssignmentDocumentsRequest } from 'src/pages/market-information/players/operators-detail/use-tso-assignment-documents';
import { useTsoRegistrationContact } from 'src/pages/market-information/players/operators-detail/use-tso-registration-contact';
import { BalancingGroups } from 'src/pages/operators/assignments/details/components/assignment-details-balancing-groups';
import { AssignmentDetailsContactSection } from 'src/pages/operators/assignments/details/components/assignment-details-contact';
import { AssignmentDetailsContractingServices } from 'src/pages/operators/assignments/details/components/assignment-details-contracting-services';
import { CreditLimits } from 'src/pages/operators/assignments/details/components/assignment-details-credit-limits';
import { AssignmentDetailsOrganisationStatus } from 'src/pages/operators/assignments/details/components/assignment-details-organization-status';
import { PortfolioCodes } from 'src/pages/operators/assignments/details/components/assignment-details-portfolio-codes';
import { AssignmentDetailsUserStatus } from 'src/pages/operators/assignments/details/components/assignment-details-user-status';
import { Contracts } from 'src/pages/operators/assignments/details/components/contracts';
import { FinancialSecurities } from 'src/pages/operators/assignments/details/components/financial-securities';
import { usePageParams } from 'src/pages/operators/assignments/details/components/use-page-params';
import { MarketType } from 'src/pages/operators/assignments/overview/types';
import { ShipperUserRequest } from 'src/pages/shipper-admin/details/use-shipper-user';
import {
  ShipperRegistrationPublicRequest,
  ShipperRegistrationRequest,
} from 'src/pages/shippers/useShipperRegistration';
import { useMemoOne } from 'use-memo-one';

export const Page: FC<PageProps> = (props) =>
  props.contractManagement.response?.data._links.operator?.href &&
  props.tso.response?.data.organizationId ? (
    <WithCMOperator {...props} />
  ) : (
    <OperatorDetailsPage {...props} />
  );

type PageProps = {
  tsoId: string;
  userId: string;
  tso: Successful<TsoRequest>;
  contractManagement: ContractManagementRequest;
  shipperUser: Successful<ShipperUserRequest>;
  shipperRegistrationPublic: Successful<ShipperRegistrationPublicRequest>;
  shipperRegistration: Successful<ShipperRegistrationRequest>;
  organisationAssignments: Successful<OrganisationAssignmentsRequest>;
  userAssignment: Successful<UserAssignmentRequest>;
  assignmentDocuments: Successful<AssignmentDocumentsRequest>;
  contractManagementOperator?: OperatorRequest;
  caMaOperator?: CaMaOperatorRequest;
};

const OperatorDetailsPage: FC<PageProps> = ({
  tsoId,
  userId,
  tso,
  shipperUser,
  shipperRegistrationPublic,
  shipperRegistration,
  organisationAssignments,
  userAssignment,
  assignmentDocuments,
  contractManagementOperator,
  caMaOperator,
}) => {
  useTitle(tso.response.data.name);
  const referrer = useReferrer({
    label: 'Operators',
    location: '/operators/assignments/overview',
  });
  const { minDesktop } = useBreakpoints();
  const pageParams = usePageParams();

  const tsoRegistrationContact = useTsoRegistrationContact({
    tsoId,
    url: shipperRegistrationPublic.response.data._links.tsoRegistrationContact
      .href,
  });

  const registrationInformationDocumentUrl =
    shipperRegistration.response.data._links?.registrationInformationDocument
      ?.href;

  const tsoAssignment = organisationAssignments.response.data.find(
    (assignedOperator) => assignedOperator.tso.id.toString() === tsoId
  );

  const userAssignmentData = userAssignment.response.data;

  const relationParams = useMemoOne<RelationsParams>(
    () => ({
      offset: pageParams.relations.value.start,
      limit: pageParams.relations.value.pageSize,
    }),
    [pageParams.relations.value.start, pageParams.relations.value.pageSize]
  );
  const financialSecuritiesParams = useMemoOne<FinancialSecuritiesParams>(
    () => ({
      offset: pageParams.financialSecurities.value.start,
      limit: pageParams.financialSecurities.value.pageSize,
    }),
    [
      pageParams.financialSecurities.value.start,
      pageParams.financialSecurities.value.pageSize,
    ]
  );
  const relations = useOperatorRelations({
    url: contractManagementOperator?.response?.data._links?.getRelations?.href,
    params: relationParams,
  });
  const financialSecurities = useFinancialSecurities({
    url: contractManagementOperator?.response?.data._links
      ?.getFinancialSecurities?.href,
    params: financialSecuritiesParams,
  });

  if (!tsoAssignment) {
    // Type safety check, should never happen
    return <ThrowNotFound />;
  }

  const tsoAssignmentMapped = {
    organisationId: tsoAssignment.tso.id,
    organisationName: tsoAssignment.tso.name,
    transferCompanyData: tsoAssignment.transferCompanyData,
    status: tsoAssignment.state,
    logoUrl: `${PRISMA_CONFIG.monolithUrl}${tsoAssignment.tso.logoSmallUrl}`,
    marketAccess:
      tsoAssignment.state !== 'APPROVED'
        ? undefined
        : ([
            ...(tsoAssignment.primaryAccess ? ['PRIMARY'] : []),
            ...(tsoAssignment.secondaryAccess ? ['SECONDARY'] : []),
          ] as MarketType),
  };

  if (
    !(relations.response || relations.error) ||
    !(tsoRegistrationContact.response || tsoRegistrationContact.error) ||
    !(financialSecurities.response || financialSecurities.error)
  )
    return <PageSpinner />;

  return (
    <Stack gap={2}>
      {referrer.backLink}

      <Heading mode="section">{tso.response.data.name}</Heading>

      <AssignmentDetailsContactSection
        tso={tso}
        tsoRegistrationContact={tsoRegistrationContact}
      />

      <Stack
        gap={1}
        templateColumns={minDesktop ? '1fr 1fr' : '1fr'}
        alignItems="stretch"
      >
        <AssignmentDetailsOrganisationStatus
          orgAssignment={tsoAssignmentMapped}
          caMaOperator={caMaOperator}
          title="Organisation Status"
        />
        <AssignmentDetailsUserStatus
          tsoId={tsoId}
          userId={userId}
          shipperUser={shipperUser}
          userAssignment={userAssignment}
          orgAssignment={tsoAssignmentMapped}
          onCancelSuccess={() => userAssignment.refresh?.()}
        />
      </Stack>

      <AssignmentDetailsContractingServices tso={tso} />

      {relations.response && contractManagementOperator?.response?.data ? (
        <Contracts
          tsoId={tsoId}
          operator={contractManagementOperator.response.data}
          relations={relations}
        />
      ) : null}

      {contractManagementOperator?.response?.data && (
        <FinancialSecurities
          tso={tso}
          operator={contractManagementOperator.response.data}
          financialSecurities={financialSecurities}
        />
      )}

      <TsoDocumentList
        tso={tso.response.data}
        assignmentDocuments={assignmentDocuments.response.data}
        registrationInformationDocumentUrl={registrationInformationDocumentUrl}
      />

      {userAssignmentData.state !== 'NOT_REQUESTED' && (
        <>
          {userAssignmentData.balancingGroups.length > 0 && (
            <BalancingGroups
              operatorOldId={tso.response.data.id}
              balancingGroups={userAssignmentData.balancingGroups}
            />
          )}

          {userAssignmentData.limits.length > 0 && (
            <CreditLimits
              operatorOldId={tso.response.data.id}
              creditLimits={userAssignmentData.limits}
            />
          )}

          {userAssignmentData.portfolioCodes.length > 0 && (
            <PortfolioCodes
              operatorOldId={tso.response.data.id}
              portfolioCodes={userAssignmentData.portfolioCodes}
            />
          )}
        </>
      )}
    </Stack>
  );
};

const WithCMOperator: FC<PageProps> = (props) => {
  const contractManagementOperator = useOperator({
    operatorUrl: props.contractManagement.response!.data._links.operator!.href,
    operatorUuid: props.tso.response.data.organizationId,
  });

  if (!contractManagementOperator.response && !contractManagementOperator.error)
    return <PageSpinner />;

  return (
    <OperatorDetailsPage
      {...props}
      contractManagementOperator={contractManagementOperator}
    />
  );
};

const PageWrapper: FC<PageProps> = (props) =>
  props.contractManagement.response?.data._links.operator?.href &&
  props.tso.response?.data.organizationId ? (
    <WithCMOperator {...props} />
  ) : (
    <Page {...props} />
  );

export default PageWrapper;
